import {Card, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {AffiliateMerchant, useAffiliateMerchantsQuery} from 'api/generated';
import {AffiliateFavIcon} from 'components/AffiliateFavIcon';
import {TableCard} from 'components/TableCard';
import {truncate} from 'lib/utils';
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import {SectionProps} from '../utils/types';
import {formatDate} from 'lib/formaters';

// Function to format the years passed since the first conversion date
const formatYearsPassed = (years: number, tier: string) => {
  if (tier === '1') {
    if (years === 0) {
      return '1st year (20%)'; // 20% for 1st year
    } else if (years === 1) {
      return '2nd year (10%)'; // 10% for 2nd year
    } else {
      return '3rd year and beyond (5%)'; // 5% for 3rd year and beyond
    }
  } else if (tier === '2') {
    if (years === 0) {
      return '1st year (10%)'; // 10% for 1st year
    } else if (years === 1) {
      return '2nd year (5%)'; // 5% for 2nd year
    } else {
      return '3rd year and beyond (2%)'; // 2% for 3rd year and beyond
    }
  }
  return 'N/A';
};

const getCommissionPercentage = (firstConversionDate: string | null, tier: string = '2') => {
  if (!firstConversionDate) {
    return formatYearsPassed(0, tier);
  }

  const conversionDate = moment(firstConversionDate, 'DD/MM/YYYY');

  // Check how many years have passed since the first conversion date
  const now = moment();
  const diffInYears = now.diff(conversionDate, 'years');

  return formatYearsPassed(diffInYears, tier);
};

export const AffiliateMerchants: React.FC<SectionProps> = ({affiliate, cardProps}) => {
  const [page, setPage] = useState(1);
  const history = useHistory();

  const {data, loading, previousData} = useAffiliateMerchantsQuery({
    variables: {
      input: {
        affiliateId: affiliate?.id!,
        nextToken: page.toString()
      }
    },
    skip: !affiliate
  });

  if (!affiliate) return <Card {...cardProps} title="Affiliate merchants" />;

  const payload = data ?? previousData;

  const merchants = payload?.affiliateMerchants.items || [];

  // Extract last page number from links
  const links = payload?.affiliateMerchants.links || '';
  const lastPageMatch = links.match(/page=(\d+)>; rel="last"/);
  const lastPage = lastPageMatch ? parseInt(lastPageMatch[1]) : 1;
  const total = merchants.length === 0 && page === 1 ? 0 : lastPage * 25; // Assuming 25 items per page

  const columns: Array<ColumnProps<AffiliateMerchant>> = [
    {
      key: 'favicon',
      dataIndex: ['business', 'website'],
      className: 'minimized-cell',
      onCell: (merchant) => ({
        onClick() {
          history.push({
            pathname: `/accounts/${merchant.meta_data?.account_id}`
          });
        }
      }),
      render(_website: string, merchant: AffiliateMerchant) {
        return <AffiliateFavIcon website={merchant.meta_data?.website} shape="square" />;
      }
    },
    {
      title: 'Name',
      dataIndex: 'name',
      onCell: (merchant) => ({
        onClick(e) {
          e.preventDefault();
          const url = `/accounts/${merchant.meta_data?.account_id}`;
          if (e.ctrlKey || e.metaKey) {
            window.open(url, merchant.meta_data?.account_id)?.focus();
          } else {
            history.push({
              pathname: url
            });
          }
        }
      }),
      render(_id: string, merchant: AffiliateMerchant) {
        return truncate(merchant.meta_data?.company_name || '', 35);
      }
    },
    {
      title: 'Actual Commission Percentage',
      dataIndex: 'commission_percentage',
      render(_commissionPercentage: string, merchant: AffiliateMerchant) {
        return getCommissionPercentage(
          merchant.meta_data?.first_conversion_date,
          affiliate.meta_data?.tier
        );
      }
    },
    {
      title: 'First Conversion Date',
      dataIndex: 'first_conversion_date',
      sorter: (a, b) => {
        return moment(a.meta_data?.first_conversion_date, 'DD/MM/YYYY').diff(
          moment(b.meta_data?.first_conversion_date, 'DD/MM/YYYY')
        );
      },
      render(_firstConversionDate: string, merchant: AffiliateMerchant) {
        return formatDate(merchant.meta_data?.first_conversion_date) ?? 'No conversions';
      }
    },
    {
      title: 'Added at',
      dataIndex: 'created_at',
      sorter: (a, b) => moment(a.created_at).diff(moment(b.created_at)),
      render(_createdAt: string, merchant: AffiliateMerchant) {
        return formatDate(merchant.created_at);
      }
    }
  ];

  return (
    <TableCard title="Merchants" {...cardProps}>
      <Table
        rowKey="id"
        scroll={{x: true}}
        dataSource={merchants}
        columns={columns as any}
        loading={loading}
        pagination={{
          pageSize: 25,
          current: page / 25,
          total: total,
          onChange: (page) => setPage(page)
        }}
      />
    </TableCard>
  );
};
